import Croppie from 'croppie';

export default class ImageCropper {
    static initAll() {
        // We'll initialize the profile image cropper for any page that has the avatar upload field
        const avatarInput = document.querySelector('input[name="--profile[user][avatar][resource]"]');
        if (avatarInput) {
            new ImageCropper(avatarInput);
        }
    }

    constructor(avatarInput) {
        this.avatarInput = avatarInput;
        this.formGroup = avatarInput.closest('.form__group');
        this.label = this.formGroup.querySelector('label[for="avatar"]');
        this.clearButton = this.formGroup.querySelector('.file-name__clear');

        // Find the existing image preview elements (adjust selectors if structure differs)
        this.deleteCheckboxGroup = document.getElementById('profileUserAvatarPreviewFormGroup');
        this.existingImagePreviewGroup = document.getElementById('profileUserAvatarClearCheckboxFormGroup');
        this.existingImagePreview = this.existingImagePreviewGroup?.querySelector('img');

        this.createModalElements();
        this.initializeCroppie();
        this.addEventListeners();
    }

    createModalElements() {
        // Create modal overlay
        this.modalOverlay = document.createElement('div');
        this.modalOverlay.classList.add('image-cropper-modal__overlay');
        this.modalOverlay.style.display = 'none';

        // Create modal container
        this.modalContainer = document.createElement('div');
        this.modalContainer.classList.add('image-cropper-modal__container');

        // Create modal content area
        this.modalContent = document.createElement('div');
        this.modalContent.classList.add('image-cropper-modal__content');

        // Create close button
        this.modalCloseButton = document.createElement('button');
        this.modalCloseButton.type = 'button';
        this.modalCloseButton.classList.add('image-cropper-modal__close-button');
        this.modalCloseButton.innerHTML = '&times;'; // '×' symbol
        this.modalCloseButton.setAttribute('aria-label', 'Close cropping modal');

        // Create Croppie container (will be placed *inside* modal)
        this.cropContainer = document.createElement('div');
        this.cropContainer.classList.add('image-cropper__container');
        // No display:none here, controlled by modal visibility

        // Create Confirm button (will be placed *inside* modal)
        this.cropConfirmButton = document.createElement('button');
        this.cropConfirmButton.type = 'button';
        this.cropConfirmButton.classList.add('button', 'button--primary', 'image-cropper__confirm-button');
        this.cropConfirmButton.textContent = 'Bild zuschneiden'; // "Crop Image"

        // Assemble modal structure
        this.modalContent.appendChild(this.cropContainer);
        this.modalContent.appendChild(this.cropConfirmButton);
        this.modalContainer.appendChild(this.modalCloseButton);
        this.modalContainer.appendChild(this.modalContent);
        this.modalOverlay.appendChild(this.modalContainer);

        // Append overlay to body
        document.body.appendChild(this.modalOverlay);
    }

    initializeCroppie() {
        this.croppie = new Croppie(this.cropContainer, {
            viewport: { width: 200, height: 200, type: 'circle' },
            boundary: { width: 300, height: 300 }, // Boundary should be slightly larger than viewport
            enableOrientation: true,
            enableZoom: true,
            showZoomer: true,
        });
    }

    addEventListeners() {
        this.avatarInput.addEventListener('change', this.handleFileChange.bind(this));
        this.cropConfirmButton.addEventListener('click', this.handleCropConfirm.bind(this));
        this.modalCloseButton.addEventListener('click', this.hideModal.bind(this));
        this.modalOverlay.addEventListener('click', (e) => {
            // Close only if clicking the overlay itself, not the container/content
            if (e.target === this.modalOverlay) {
                this.hideModal();
            }
        });
        if (this.clearButton) {
            this.clearButton.addEventListener('click', this.handleClear.bind(this));
        }
    }

    showModal() {
        this.modalOverlay.style.display = 'flex'; // Use flex to help center container
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    hideModal() {
        this.modalOverlay.style.display = 'none';
        document.body.style.overflow = ''; // Restore background scrolling
        // Optional: Reset input if modal is closed without confirming
        // this.avatarInput.value = ''; // Uncomment if closing should discard selection
    }

    bindCroppieImage(imageUrl) {
        // Ensure Croppie binds only when the container is truly visible and rendered
        requestAnimationFrame(() => {
             this.cropContainer.classList.add('image-cropper__container--is-loading');
             this.croppie.bind({ url: imageUrl }).then(() => {
                 console.log('Croppie bind complete');
                 this.cropContainer.classList.remove('image-cropper__container--is-loading');
             }).catch(err => {
                console.error("Croppie bind error:", err);
                this.cropContainer.classList.remove('image-cropper__container--is-loading');
                this.hideModal(); // Hide modal on bind error
                // Optionally show an error message to the user
             });
        });
    }

    handleFileChange() {
        if (!this.avatarInput.files || !this.avatarInput.files[0]) return;

        this.file = this.avatarInput.files[0];
        const reader = new FileReader();

        reader.onload = (evt) => {
            // Hide the label and clear button in the main form temporarily
             this.label.style.display = 'none';
             if(this.clearButton) this.clearButton.style.display = 'none';

             // Show the modal
            this.showModal();
             // Bind the image inside the modal
            this.bindCroppieImage(evt.target.result);
        };

        reader.onerror = (err) => {
            console.error("FileReader error:", err);
            // Handle FileReader errors, e.g., show a message
        };

        reader.readAsDataURL(this.file);
    }

    handleCropConfirm() {
        this.cropContainer.classList.add('image-cropper__container--is-loading'); // Show loading state on confirm
        this.cropConfirmButton.disabled = true; // Disable button during processing

        this.croppie.result({
            type: 'blob',
            size: { width: 400, height: 400 },
            format: 'png',
            quality: 0.9,
            circle: false
        }).then((blob) => {
            const fileNameWithoutExtension = this.file.name.replace(/\.[^/.]+$/, "");
            const croppedFile = new File([blob], fileNameWithoutExtension + '.png', { type: "image/png", lastModified: Date.now() });
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(croppedFile);
            this.avatarInput.files = dataTransfer.files;

            // Update label and show clear button in the main form
            this.label.textContent = croppedFile.name;
            this.label.style.display = ''; // Restore display
            if(this.clearButton) this.clearButton.style.display = ''; // Restore display

            // Hide the original preview and delete button if they exist
            if (this.deleteCheckboxGroup) this.deleteCheckboxGroup.style.display = 'none';
            if (this.existingImagePreviewGroup) this.existingImagePreviewGroup.style.display = 'none';

            this.hideModal();
        }).catch(err => {
            console.error("Croppie result error:", err);
            // Handle result errors, maybe show a message
        }).finally(() => {
             // Always remove loading state and re-enable button
             this.cropContainer.classList.remove('image-cropper__container--is-loading');
             this.cropConfirmButton.disabled = false;
        });
    }

    handleClear() {
        // Hide modal if open
        this.hideModal();

        // Reset the label to its default placeholder text
        const placeholder = this.label.dataset.placeholder || 'Neues Profilbild auswählen';
        this.label.textContent = placeholder;
        this.label.style.display = ''; // Ensure label is visible

        // Show the existing image preview and delete checkbox again if they exist and were hidden
        if (this.deleteCheckboxGroup) {
            this.deleteCheckboxGroup.style.display = ''; // Restore display
        }
        if (this.existingImagePreviewGroup) {
            this.existingImagePreviewGroup.style.display = ''; // Restore display
        }

        // Destroy and reinitialize Croppie instance to clear state
        // Check if destroy is needed or if a reset method exists
         if (this.croppie) {
            this.croppie.destroy();
         }
         this.initializeCroppie(); // Re-create it

        // Clear the file input value
        this.avatarInput.value = '';
    }
}
